import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router'

import Api from '../utils/api'
import { MESHIFYID_TOKEN, SIDEWALKID_TOKEN, VERIFY_FOLDER_ID, VERIFY_NODE_ID } from '../utils/constants'

export interface ActivationLocationState {
  nodeTypeName: 'nbiot_leak' | 'leak5s'
  nodeId: number
  folderId: number
  meshifyId: string
  sidewalkId: string
}

export const ActivationContext = createContext<{
  loading: boolean
  leakFreezeNotInstalled: number
  isFirstSlim: boolean
  setIsFirstSlim: (value: boolean) => void
  activationLocationState: ActivationLocationState
  getDeviceActivationCheck: () => Promise<void>
}>({
  loading: true,
  leakFreezeNotInstalled: 0,
  isFirstSlim: true,
  setIsFirstSlim: () => {},
  activationLocationState: { nodeTypeName: 'leak5s', nodeId: 0, folderId: 0, meshifyId: '', sidewalkId: '' },
  getDeviceActivationCheck: async () => undefined,
})

export const sanitizeId = (id: string, typecase?: 'uppercase' | 'lowercase') => {
  const sanitizedStr = id.replace(/[^a-zA-Z0-9]/g, '').slice(0, 7) // Keep only alphanumeric characters and limit to 7 characters
  return typecase === 'uppercase' ? sanitizedStr.toUpperCase() : typecase === 'lowercase' ? sanitizedStr.toLowerCase() : sanitizedStr
}

const ActivationProvider: FC<PropsWithChildren> = ({ children }) => {
  const location: {
    state: {
      fromCompleteSetup?: boolean
    } & ActivationLocationState
    pathname: string
  } = useLocation()

  const {
    nodeTypeName: stateNodeTypeName,
    nodeId: stateNodeId,
    folderId: stateFolderId,
    meshifyId: stateMeshifyId,
    sidewalkId: stateSidewalkId,
    fromCompleteSetup,
  } = location.state || {}

  const [loading, setLoading] = useState<boolean>(false)
  const [leakFreezeNotInstalled, setLeakFreezeNotInstalled] = useState<number>(0)
  const [isFirstSlim, setIsFirstSlim] = useState<boolean>(true)
  const storageMeshifyId = sanitizeId(localStorage.getItem(MESHIFYID_TOKEN) || '', 'uppercase')
  const storageSidewalkId = sanitizeId(localStorage.getItem(SIDEWALKID_TOKEN) || '', 'lowercase')
  const storageNodeId = localStorage.getItem(VERIFY_NODE_ID)
  const storageFolderId = localStorage.getItem(VERIFY_FOLDER_ID)
  const nodeTypeName = stateNodeTypeName || 'leak5s'
  const meshifyId = stateMeshifyId || (!fromCompleteSetup ? storageMeshifyId : '')
  const sidewalkId = stateSidewalkId || (!fromCompleteSetup ? storageSidewalkId : '')
  const nodeId = stateNodeId || (storageNodeId && !fromCompleteSetup ? Number(storageNodeId) : 0)
  const folderId = stateFolderId || (storageFolderId && !fromCompleteSetup ? Number(storageFolderId) : 0)
  const activationLocationState = { nodeTypeName, nodeId, folderId, meshifyId, sidewalkId }

  // Check "device-activation-check" to check if this is the first leak5s being installed and how many devices are left to install
  const getDeviceActivationCheck = useCallback(async () => {
    setLoading(true)
    try {
      const res = await Api.get(`/api/v2/protect/folders/${folderId}/device-activation-check`)
      if (res.ok) {
        // Get the count of leak & freeze devices left to be installed; this is used at the end of activating a device to route the user to the dashboard if they can't activate more devices or to the device roster if they can
        const leakFreezeDevices = res.body.devices.find((device: ICanActivateDeviceResponse) => device.nodeTypeName === 'nbiot_leak')
        setLeakFreezeNotInstalled(leakFreezeDevices?.totalDevicesNotInstalled)
      }
    } catch {
      // Do nothing
    } finally {
      setLoading(false)
    }
  }, [folderId])
  useEffect(() => {
    if (!folderId) return
    getDeviceActivationCheck()
  }, [folderId, getDeviceActivationCheck, nodeTypeName])

  return (
    <ActivationContext.Provider
      value={{
        loading,
        leakFreezeNotInstalled,
        isFirstSlim,
        setIsFirstSlim,
        activationLocationState,
        getDeviceActivationCheck,
      }}
    >
      {children}
    </ActivationContext.Provider>
  )
}

export default ActivationProvider
export const useActivationContext = () => useContext(ActivationContext)
